/**
 * **Add a day** — `docs/visit-page.md` §15.2, Board 11 B (popup 470; on a
 * phone Board 16's sheet from the bottom). Opened from the Days card's dashed
 * tile, the header's ⋯ and the phone's **+ Add**.
 *
 * *V-1187 · becomes Day 4 of 4, after Fri Oct 23*. **Day**: a strip of the
 * next working days with the forecast — Sunday *off* and dimmed, days the
 * crew is full dimmed — and *Hardscape 2 is free Mon and Tue · weather from
 * the National Weather Service*; **Crew** and **Hours**; **Where the hours
 * come from** — *Split them off Day 3* (the default, D16) or *They are
 * extra*; **Why** (optional). **Add the day** creates the `visits` row
 * through `scheduleVisit` and renumbers the days by date.
 *
 * The strip, the crew's free days and both choices' sentences are the
 * server's (`GET …/days/add-options?hours=&crewId=`), asked again as the hours
 * or the crew change — the page works out none of them.
 */
import { useEffect, useRef, useState } from 'react'
import type { AddDayOptions, VisitRead } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { useDismiss } from '../components/overlays.js'
import { useVisitTab, useVisitWrite } from './api.js'
import { FaceRow } from './face.js'
import { VisitGlyph } from './glyphs.js'
import { VisitButton, VisitPopup, VisitTile, VISIT_LAYER, VISIT_LAYER_OVER } from './sheet.js'
import { Sky } from './day-parts.js'
import { FIELD_BOX, FIELD_INPUT, FieldLabel, RadioDot } from './fields.js'
import { ADD_DAY } from './copy.js'
import { dayNumber, dayText, hoursBare } from './format.js'

type Crew = AddDayOptions['crews'][number]

export function AddDayPopup({ visitId, read, phone: forced, layer = VISIT_LAYER, onClose, onAdded }: {
  visitId: string
  read: VisitRead
  phone?: boolean
  layer?: number
  onClose: () => void
  /** After **Add the day** — the page has the fresh Overview already. */
  onAdded?: () => void
}) {
  const windowPhone = usePhone()
  const phone = forced ?? windowPhone
  const write = useVisitWrite(visitId)

  const [hours, setHours] = useState('')
  const [asked, setAsked] = useState<{ hours?: string; crewId?: string }>({})
  const options = useVisitTab<AddDayOptions>(visitId, '/days/add-options', asked)
  const data = options.data

  const [date, setDate] = useState<string | null>(null)
  const [crewId, setCrewId] = useState<string | null>(null)
  const [hoursFrom, setHoursFrom] = useState<'split' | 'extra' | null>(null)
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* The server's defaults, the first time they arrive. */
  useEffect(() => {
    if (!data) return
    setDate((was) => was ?? data.crewFreeOn[0] ?? data.strip.find((one) => !one.off)?.date ?? null)
    setCrewId((was) => was ?? data.crew?.crew.id ?? null)
    setHoursFrom((was) => was ?? (data.split ? data.defaultHoursFrom : 'extra'))
    setHours((was) => (was === '' ? hoursBare(data.hours) : was))
  }, [data])

  /* *Where the hours come from* is re-worked for the hours typed — asked a moment after the typing stops. */
  useEffect(() => {
    const typed = hours.trim()
    if (!typed || !/^\d+(\.\d+)?$/.test(typed)) return
    const timer = window.setTimeout(() => {
      setAsked((was) => (was.hours === typed ? was : { ...was, hours: typed }))
    }, 250)
    return () => window.clearTimeout(timer)
  }, [hours])

  const crews = data?.crews ?? []
  const crew = crews.find((one) => one.crew.id === crewId) ?? (data?.crew ? { ...data.crew, free: true } : null)
  const hoursValid = /^\d+(\.\d+)?$/.test(hours.trim()) && Number(hours) > 0 && Number(hours) <= 24
  const ready = Boolean(data && date && crewId && hoursFrom && hoursValid) && !busy

  async function add() {
    if (!ready || !data || !date || !crewId || !hoursFrom) return
    setBusy(true)
    setError(null)
    try {
      await write('POST', '/days', {
        date, crewId, hours: Number(hours), hoursFrom,
        ...(hoursFrom === 'split' && data.split ? { splitFromDay: data.split.fromDay } : {}),
        reason: why.trim(),
      }, { tab: 'overview' })
      onAdded?.()
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(false)
    }
  }

  const size = phone ? 'phone' : 'popup'
  const becomes = data?.becomes
  return (
    <VisitPopup
      width={470}
      phone={phone}
      title={ADD_DAY.title}
      sub={becomes && becomes.after
        ? ADD_DAY.sub(read.reference, String(becomes.index), String(becomes.count), dayText(becomes.after, 'date'))
        : read.reference}
      mark={<VisitTile glyph="calendar" tone="accent" size={34} />}
      onClose={onClose}
      layer={layer}
      asDrawn="vp-11 B"
      footer={(
        <>
          <VisitButton size={size} onClick={onClose}>{ADD_DAY.cancel}</VisitButton>
          <VisitButton size={size} tone="primary" glyph="plus" disabled={!ready} onClick={() => void add()}>
            {ADD_DAY.add}
          </VisitButton>
        </>
      )}
    >
      {data && (
        <div className="flex flex-col gap-3">
          <div className="min-w-0">
            <FieldLabel>{ADD_DAY.day}</FieldLabel>
            <div role="radiogroup" aria-label={ADD_DAY.day} className="flex gap-1">
              {data.strip.map((one) => {
                const on = one.date === date
                return (
                  <button
                    key={one.date}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={one.off}
                    onClick={() => setDate(one.date)}
                    className={cn(
                      'flex flex-1 flex-col items-center gap-[3px] rounded-[10px] border-[1.5px] py-2',
                      on ? 'border-accent bg-green-band-bg shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]'
                        : 'border-transparent hover:bg-hover',
                      (one.off || one.crewFull) && !on && 'opacity-45',
                      one.off && 'cursor-not-allowed hover:bg-transparent',
                    )}
                  >
                    <span className="text-[10.5px] font-semibold uppercase text-text-muted">{dayText(one.date, 'day')}</span>
                    <b className="text-[15px]">{dayNumber(one.date)}</b>
                    {one.off
                      ? <span className="text-[10px] text-text-faint">{ADD_DAY.off}</span>
                      : one.forecast && (
                        <span className="inline-flex items-center gap-[5px] whitespace-nowrap text-[10px] leading-none">
                          <Sky icon={one.forecast.icon} size={12} />
                          {one.forecast.high !== null && <b className="font-bold text-text">{one.forecast.high}°</b>}
                        </span>
                      )}
                  </button>
                )
              })}
            </div>
            {data.crew && data.crewFreeOn.length > 0 && (
              <div className="mt-1 text-[11px] leading-[1.4] text-text-faint">
                {ADD_DAY.free(data.crew.crew.name, data.crewFreeOn.map((day) => dayText(day, 'day')))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-x-3.5 gap-y-3">
            <div className="min-w-0">
              <FieldLabel>{ADD_DAY.crew}</FieldLabel>
              <CrewField
                crews={crews}
                chosen={crew}
                phone={phone}
                onPick={(id) => { setCrewId(id); setAsked((was) => ({ ...was, crewId: id })) }}
              />
            </div>
            <div className="min-w-0">
              <FieldLabel htmlFor="add-day-hours">{ADD_DAY.hours}</FieldLabel>
              <div className={FIELD_BOX}>
                <input
                  id="add-day-hours"
                  inputMode="decimal"
                  value={hours}
                  onChange={(event) => setHours(event.target.value)}
                  className={cn(FIELD_INPUT, 'tabular-nums')}
                />
                <span className="ml-1.5 text-[12px] text-text-faint">{ADD_DAY.unit}</span>
              </div>
            </div>
          </div>

          <div className="min-w-0">
            <FieldLabel>{ADD_DAY.whereFrom}</FieldLabel>
            <div role="radiogroup" aria-label={ADD_DAY.whereFrom} className="flex flex-col gap-[7px]">
              {data.split && (
                <HoursChoice
                  on={hoursFrom === 'split'}
                  onClick={() => setHoursFrom('split')}
                  title={ADD_DAY.split(String(data.split.fromDay))}
                  sub={ADD_DAY.splitSub(String(data.split.fromDay), hoursBare(data.split.fromHours),
                    hoursBare(data.split.toHours), hoursBare(data.split.visitPlans))}
                />
              )}
              <HoursChoice
                on={hoursFrom === 'extra'}
                onClick={() => setHoursFrom('extra')}
                title={ADD_DAY.extra}
                sub={ADD_DAY.extraSub(hoursBare(data.extra.planned), hoursBare(data.extra.overEstimate))}
              />
            </div>
          </div>

          <div className="min-w-0">
            <FieldLabel htmlFor="add-day-why" optional={ADD_DAY.optional}>{ADD_DAY.why}</FieldLabel>
            <div className={FIELD_BOX}>
              <input id="add-day-why" value={why} onChange={(event) => setWhy(event.target.value)} className={cn(FIELD_INPUT, 'tabular-nums')} />
            </div>
          </div>

          {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
        </div>
      )}
    </VisitPopup>
  )
}

/** One answer to *Where the hours come from* — a 1.5px card, green when chosen (Board 11 B). */
function HoursChoice({ on, onClick, title, sub }: { on: boolean; onClick: () => void; title: string; sub: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      onClick={onClick}
      className={cn('flex items-start gap-2.5 rounded-[10px] border-[1.5px] px-3 py-2.5 text-left',
        on ? 'border-accent bg-green-band-bg' : 'border-line bg-surface hover:bg-hover')}
    >
      <RadioDot on={on} size={17} />
      <span className="flex min-w-0 flex-col">
        <b className="text-[12.5px]">{title}</b>
        <span className="text-[11.5px] text-text-muted">{sub}</span>
      </span>
    </button>
  )
}

/**
 * **Crew** — the faces and the name in a 36px box with its chevron (Board 11
 * B); the crews in a menu under it, free crews first; on a phone the same
 * rows in a sheet from the bottom.
 */
function CrewField({ crews, chosen, phone, onPick }: {
  crews: Crew[]
  chosen: Crew | null
  phone: boolean
  onPick: (crewId: string) => void
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useDismiss(open && !phone, () => setOpen(false), box, VISIT_LAYER_OVER + 1)

  const rows = (
    <div role="listbox" aria-label={ADD_DAY.crew} className="flex flex-col py-1">
      {crews.map((one) => (
        <button
          key={one.crew.id}
          type="button"
          role="option"
          aria-selected={one.crew.id === chosen?.crew.id}
          onClick={() => { onPick(one.crew.id); setOpen(false) }}
          className={cn('flex items-center gap-2 px-3 text-left hover:bg-hover',
            phone ? 'h-12 text-[14.5px]' : 'h-9 text-[13px]',
            !one.free && 'opacity-45')}
        >
          <FaceRow people={one.people} size={20} />
          <span className="min-w-0 flex-1 truncate">{one.crew.name}</span>
          {one.crew.id === chosen?.crew.id && <VisitGlyph name="check" size={14} stroke={2.4} />}
        </button>
      ))}
    </div>
  )

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ADD_DAY.crew}
        onClick={() => setOpen((was) => !was)}
        className={cn(FIELD_BOX, 'gap-2 text-left', open && 'border-accent-line')}
      >
        {chosen && <FaceRow people={chosen.people} size={20} />}
        <span className="min-w-0 flex-1 truncate">{chosen?.crew.name}</span>
        <span className="text-text-faint"><VisitGlyph name="chevronDown" size={13} stroke={2} /></span>
      </button>
      {open && !phone && crews.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 overflow-hidden rounded-[12px] border border-line bg-surface shadow-popover">
          {rows}
        </div>
      )}
      {open && phone && (
        <VisitPopup width={470} phone title={ADD_DAY.crew} onClose={() => setOpen(false)} layer={VISIT_LAYER_OVER + 1}>
          {rows}
        </VisitPopup>
      )}
    </div>
  )
}
